// components/Gallery.jsx
import React, { useEffect, useRef, useState } from 'react';
import { 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight, 
  FaCamera,
  FaHeart,
  FaShare,
  FaDownload
} from 'react-icons/fa';

const Gallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Gallery images from public folder
  const galleryImages = [
    { id: 1, src: '/image1.png' },
    { id: 2, src: '/image2.png' },
    { id: 3, src: '/image3.png' },
    { id: 4, src: '/image4.png' },
    { id: 5, src: '/image5.png' },
    { id: 6, src: '/image6.png'},
    { id: 7, src: '/image7.png'},
    { id: 8, src: '/image8.png' },
    { id: 9, src: '/image9.png' },
  ];

  const categories = ['All', 'Rooms', 'Bathroom', 'Common Areas', 'Dining', 'Amenities', 'Views'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(filteredImages[index]);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (currentIndex < filteredImages.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedImage(filteredImages[currentIndex + 1]);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedImage(filteredImages[currentIndex - 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === 'ArrowLeft') prevImage();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'Escape') closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  return (
    <section ref={sectionRef} className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider bg-blue-50 px-4 py-1.5 rounded-full inline-block mb-3">
            Our Gallery
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Capturing <span className="text-blue-600">Moments</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            Take a visual tour of our beautiful property and amenities
          </p>
        </div>

        {/* Category Filters */}
        <div className={`flex flex-wrap justify-center gap-2 mb-10 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className={`group relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => openLightbox(index)}
            >
              <div className="relative overflow-hidden h-64">
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x400/3b82f6/ffffff?text=Image+Coming+Soon';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                    <p className="text-white/80 text-sm">{image.category}</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-black/50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <FaCamera className="text-white text-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button 
            onClick={() => setActiveCategory('All')}
            className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300"
          >
            View All Photos
          </button>
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={closeLightbox}>
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <FaTimes size={30} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className={`absolute left-4 text-white hover:text-gray-300 transition-colors z-10 ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
              disabled={currentIndex === 0}
            >
              <FaChevronLeft size={40} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className={`absolute right-4 text-white hover:text-gray-300 transition-colors z-10 ${currentIndex === filteredImages.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
              disabled={currentIndex === filteredImages.length - 1}
            >
              <FaChevronRight size={40} />
            </button>

            <div className="max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="max-h-[80vh] w-auto mx-auto rounded-lg"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600/3b82f6/ffffff?text=Image+Coming+Soon';
                }}
              />
              <div className="text-center mt-4">
                <h3 className="text-white text-xl font-semibold">{selectedImage.title}</h3>
                <p className="text-gray-400">{selectedImage.category}</p>
                <div className="flex justify-center gap-4 mt-3">
                  <button className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
                    <FaHeart /> Like
                  </button>
                  <button className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
                    <FaShare /> Share
                  </button>
                  <a href={selectedImage.src} download className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
                    <FaDownload /> Download
                  </a>
                </div>
              </div>
            </div>

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/70 text-sm">
              {currentIndex + 1} / {filteredImages.length}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;